/**
 * Minimal structural contract the bridge needs from ThemesHelperService.
 * Any object with getFeatureFlag satisfies this — no AngularJS import needed.
 */
export interface FeatureFlagSource {
  getFeatureFlag(key: string): boolean;
}

/**
 * Encapsulates feature flag override logic as instance state.
 *
 * Constructed eagerly (before AngularJS bootstraps) with persisted overrides.
 * Connected lazily to the live service via connect() once the injector is ready.
 *
 * Follows the PluginRegistry idiom: plain class, Map as instance state, no DI.
 *
 * Resolution order for getFlag():
 *   1. overrides  — DevTools server-flag overrides
 *   2. source      — ThemesHelperService (server-side flags)
 *   3. undefined   — source not yet connected
 */
export class FeatureFlagBridge {
  private readonly overrides: Map<string, boolean>;
  private source: FeatureFlagSource | null;

  constructor(
    initialOverrides: Record<string, boolean>,
    source: FeatureFlagSource | null,
  ) {
    this.overrides = new Map(Object.entries(initialOverrides));
    this.source = source;
  }

  /** Late-bind the AngularJS service after Angular has bootstrapped. */
  connect(source: FeatureFlagSource): void {
    this.source = source;
  }

  /**
   * Override-aware read.
   * Resolution: overrides -> source -> undefined
   * Returns undefined when source not yet connected and no override exists.
   */
  getFlag(key: string): boolean | undefined {
    if (this.overrides.has(key)) {
      return this.overrides.get(key)!;
    }

    if (this.source) {
      return this.source.getFeatureFlag(key);
    }

    return undefined;
  }

  /**
   * Raw read from the source, bypassing overrides.
   * Returns undefined when source not yet connected.
   */
  getOriginalFlag(key: string): boolean | undefined {
    if (!this.source) {
      return undefined;
    }

    return this.source.getFeatureFlag(key);
  }

  /**
   * Set (value !== null) or clear (value === null) an override.
   * Persistence is the caller's responsibility.
   */
  setOverride(key: string, value: boolean | null): void {
    if (value === null) {
      this.overrides.delete(key);
    } else {
      this.overrides.set(key, value);
    }
  }

  /** Snapshot of all active overrides as a plain Record. */
  getOverrides(): Record<string, boolean> {
    return Object.fromEntries(this.overrides);
  }


}
